import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { IMAGE_CONSTRAINTS } from '../../lib/utils';

export const TeamMemberModal = ({ 
    open, 
    onOpenChange, 
    onSubmit, 
    member = null,
    mode = 'create' 
}) => {
    const [formData, setFormData] = useState({
        name: '',
        alias: '',
        role: '',
        avatar_url: '',
        social_links: {
            twitter: '',
            discord: '',
            twitch: '',
            youtube: '',
            roblox: '',
        },
    });

    useEffect(() => {
        if (open) {
            if (member && mode === 'edit') {
                setFormData({
                    name: member.name || '',
                    alias: member.alias || '',
                    role: member.role || '',
                    avatar_url: member.avatar_url || '',
                    social_links: {
                        twitter: member.social_links?.twitter || '',
                        discord: member.social_links?.discord || '',
                        twitch: member.social_links?.twitch || '',
                        youtube: member.social_links?.youtube || '',
                        roblox: member.social_links?.roblox || '',
		    },
                });
            } else {
                setFormData({
                    name: '',
                    alias: '',
                    role: '',
                    avatar_url: '',
                    social_links: {
                        twitter: '',
                        discord: '',
                        twitch: '',
                        youtube: '',
                        roblox: '',
                    },
                });
            }
        }
    }, [open, member, mode]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const updateSocialLink = (platform, value) => {
        setFormData(prev => ({
            ...prev,
            social_links: {
                ...prev.social_links,
                [platform]: value,
            },
        }));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg bg-[#0A0A0A] border-white/10">
                <DialogHeader>
                    <DialogTitle className="font-['Rajdhani'] text-2xl text-white">
                        {mode === 'create' ? 'Teammitglied hinzufügen' : 'Teammitglied bearbeiten'}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-400">Name *</Label>
                        <Input
                            id="name"
                            data-testid="member-name-input"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Anzeigename"
                            required
                            className="bg-black/40 border-white/10"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="alias" className="text-gray-400">Alias</Label>
                        <Input
                            id="alias"
                            data-testid="member-alias-input"
                            value={formData.alias}
                            onChange={(e) => setFormData(prev => ({ ...prev, alias: e.target.value }))}
                            placeholder="z.B. @username oder Spitzname"
                            className="bg-black/40 border-white/10"
                        />
                        <p className="text-xs text-gray-600">Wird klein unter dem Namen angezeigt</p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="role" className="text-gray-400">Rolle *</Label>
                        <Input
                            id="role"
                            data-testid="member-role-input"
                            value={formData.role}
                            onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                            placeholder="z.B. Developer, Designer, Community Manager"
                            required
                            className="bg-black/40 border-white/10"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="avatar_url" className="text-gray-400">
                            Avatar URL
                            <span className="text-xs text-gray-600 ml-2">
                                (max {IMAGE_CONSTRAINTS.maxWidth}x{IMAGE_CONSTRAINTS.maxHeight}px)
                            </span>
                        </Label>
                        <Input
                            id="avatar_url"
                            data-testid="member-avatar-input"
                            value={formData.avatar_url}
                            onChange={(e) => setFormData(prev => ({ ...prev, avatar_url: e.target.value }))}
                            placeholder="https://example.com/avatar.jpg"
                            className="bg-black/40 border-white/10"
                        />
                    </div>

                    <div className="space-y-3">
                        <Label className="text-gray-400">Social Links</Label>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs text-gray-500">Twitter/X</Label>
                                <Input
                                    data-testid="member-twitter-input"
                                    value={formData.social_links.twitter}
                                    onChange={(e) => updateSocialLink('twitter', e.target.value)}
                                    placeholder="https://twitter.com/..."
                                    className="bg-black/40 border-white/10"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label className="text-xs text-gray-500">Discord</Label>
                                <Input
                                    data-testid="member-discord-input"
                                    value={formData.social_links.discord}
                                    onChange={(e) => updateSocialLink('discord', e.target.value)}
                                    placeholder="Discord Invite oder Profil"
                                    className="bg-black/40 border-white/10"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label className="text-xs text-gray-500">Twitch</Label>
                                <Input
                                    data-testid="member-twitch-input"
                                    value={formData.social_links.twitch}
                                    onChange={(e) => updateSocialLink('twitch', e.target.value)}
                                    placeholder="https://twitch.tv/..."
                                    className="bg-black/40 border-white/10"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label className="text-xs text-gray-500">YouTube</Label>
                                <Input
                                    data-testid="member-youtube-input"
                                    value={formData.social_links.youtube}
                                    onChange={(e) => updateSocialLink('youtube', e.target.value)}
                                    placeholder="https://youtube.com/..."
                                    className="bg-black/40 border-white/10"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-gray-500">Roblox</Label>
                                <Input
                                    data-testid="member-roblox-input"
                                    value={formData.social_links.roblox}
                                    onChange={(e) => updateSocialLink('roblox', e.target.value)}
                                    placeholder="https://www.roblox.com/users/..."
                                    className="bg-black/40 border-white/10"
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="gap-2 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="border-white/10 text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            type="submit"
                            data-testid="submit-member-button"
                            className="bg-purple-500 hover:bg-purple-400 text-white font-semibold"
                        >
                            {mode === 'create' ? 'Hinzufügen' : 'Speichern'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
